import React, { useMemo, useState } from "react";
import { Box, Portal, Typography } from "@mui/material";

const DEFAULT_LENS_SIZE = 96;
const DEFAULT_ZOOM_SCALE = 6.5;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function isSvgSource(source) {
  if (typeof source !== "string" || !source) {
    return false;
  }

  if (source.startsWith("data:image/svg+xml")) {
    return true;
  }

  return /\.svg($|[?#])/i.test(source);
}

function HoverZoomImage(props) {
  const {
    src,
    alt,
    containerSx,
    imageSx,
    lensSize = DEFAULT_LENS_SIZE,
    zoomScale = DEFAULT_ZOOM_SCALE,
    previewSize = 420,
    borderRadius = 20,
    hint = "Hover to zoom",
    imgProps = {},
  } = props;
  const [zoomState, setZoomState] = useState({
    active: false,
    xPercent: 50,
    yPercent: 50,
    lensLeft: 0,
    lensTop: 0,
    previewLeft: 0,
    previewTop: 0,
  });
  const svgSource = isSvgSource(src);

  const sharedRadius =
    typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius;

  const previewBackgroundPosition = useMemo(
    () => `${zoomState.xPercent}% ${zoomState.yPercent}%`,
    [zoomState.xPercent, zoomState.yPercent]
  );

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const offsetX = clamp(event.clientX - rect.left, 0, rect.width);
    const offsetY = clamp(event.clientY - rect.top, 0, rect.height);
    const xPercent = (offsetX / rect.width) * 100;
    const yPercent = (offsetY / rect.height) * 100;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const gap = 20;
    const fitsRight = rect.right + gap + previewSize <= viewportWidth - 12;
    const previewLeft = fitsRight
      ? rect.right + gap
      : Math.max(12, rect.left - previewSize - gap);
    const previewTop = clamp(
      rect.top + rect.height / 2 - previewSize / 2,
      12,
      Math.max(12, viewportHeight - previewSize - 12)
    );

    setZoomState({
      active: true,
      xPercent,
      yPercent,
      lensLeft: clamp(offsetX - lensSize / 2, 0, rect.width - lensSize),
      lensTop: clamp(offsetY - lensSize / 2, 0, rect.height - lensSize),
      previewLeft,
      previewTop,
    });
  };

  const deactivateZoom = () => {
    setZoomState((previous) => ({
      ...previous,
      active: false,
    }));
  };

  return (
    <Box
      onMouseMove={handlePointerMove}
      onMouseEnter={handlePointerMove}
      onMouseLeave={deactivateZoom}
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: "100%",
        maxHeight: "100%",
        lineHeight: 0,
        ...containerSx,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        {...imgProps}
        sx={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          backgroundColor: svgSource ? "#ffffff" : undefined,
          ...imageSx,
        }}
      />

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: zoomState.lensLeft,
          top: zoomState.lensTop,
          width: lensSize,
          height: lensSize,
          borderRadius: "18px",
          border: "1px solid rgba(255,255,255,0.92)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(142,166,201,0.14))",
          boxShadow: "0 10px 24px rgba(0,0,0,0.26)",
          pointerEvents: "none",
          opacity: zoomState.active ? 1 : 0,
          transition: "opacity 120ms ease",
          display: { xs: "none", md: "block" },
          "@media (hover: none), (pointer: coarse)": {
            display: "none",
          },
        }}
      />

      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          left: 14,
          bottom: 14,
          px: 1.1,
          py: 0.45,
          borderRadius: 999,
          backgroundColor: "rgba(17,22,29,0.72)",
          color: "#edf3fb",
          letterSpacing: "0.04em",
          lineHeight: 1.2,
          pointerEvents: "none",
          opacity: zoomState.active ? 0 : 1,
          transition: "opacity 160ms ease",
          display: { xs: "none", md: "inline-flex" },
          "@media (hover: none), (pointer: coarse)": {
            display: "none",
          },
        }}
      >
        {hint}
      </Typography>

      <Portal>
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            left: zoomState.previewLeft,
            top: zoomState.previewTop,
            width: previewSize,
            height: previewSize,
            borderRadius: sharedRadius,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 22px 44px rgba(0,0,0,0.34)",
            backgroundColor: svgSource ? "#ffffff" : "rgba(17,22,29,0.96)",
            backgroundImage: `url("${src}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: previewBackgroundPosition,
            backgroundSize: `${zoomScale * 100}%`,
            pointerEvents: "none",
            zIndex: 1700,
            opacity: zoomState.active ? 1 : 0,
            transform: zoomState.active ? "translateX(0)" : "translateX(8px)",
            transition: "opacity 140ms ease, transform 140ms ease",
            display: { xs: "none", md: "block" },
            "@media (hover: none), (pointer: coarse)": {
              display: "none",
            },
          }}
        />
      </Portal>
    </Box>
  );
}

export default HoverZoomImage;
